import { AxiosError } from 'axios';
import { useMutation, useQueryClient, QueryKey } from 'react-query';
import { Axios } from '@utils/api/axios-instance.ts';
import { DELETE_URL } from '@utils/api/endpoints.ts';
import { BaseError } from '@utils/types/shared/base.ts';
import { SttSessionsResponse } from '@utils/types/stt-sessions/stt-sessions';

type Snapshot = Array<[QueryKey, SttSessionsResponse | undefined]>;
type MutationContext = { previous: Snapshot };

export const useDeleteStt = () => {
  const queryClient = useQueryClient();

  const deleteSession = async (sessionId: string): Promise<void> => {
    await Axios.delete(DELETE_URL(sessionId).deleteSttSession);
  };

  return useMutation<void, AxiosError<BaseError>, string, MutationContext>(
    deleteSession,
    {
      onMutate: async sessionId => {
        await queryClient.cancelQueries(['sttSessions']);

        const previous = queryClient.getQueriesData<SttSessionsResponse>([
          'sttSessions'
        ]) as Snapshot;

        previous.forEach(([key, data]) => {
          if (!data) return;
          const next = data.result.filter(
            r => String(r.id) !== String(sessionId)
          );
          if (next.length !== data.result.length) {
            queryClient.setQueryData<SttSessionsResponse>(key, {
              ...data,
              result: next,
              total_results:
                typeof data.total_results === 'number'
                  ? Math.max(0, data.total_results - 1)
                  : data.total_results
            });
          }
        });

        return { previous };
      },

      onError: (_err, _sessionId, ctx) => {
        ctx?.previous.forEach(([key, snapshot]) => {
          queryClient.setQueryData(key, snapshot);
        });
      },

      onSuccess: () => {
        queryClient.invalidateQueries('sttSessions', {
          refetchActive: true,
          refetchInactive: false
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } as any);
      },
      useErrorBoundary: false
    }
  );
};
